<template>
  <main class="w-full">
    <section class="w-full py-24 px-6 lg:px-24 bg-grey-dark-900">
      <div class="flex flex-col lg:flex-row items-center text-center lg:text-left gap-14">
        <div class="flex flex-col gap-4 md:gap-10 max-w-3xl">
          <p class="text-xl font-semibold text-grey-dark-300 w-full">Lay-by</p>
          <h1 class="text-5xl lg:text-7xl font-semibold text-grey-dark-50 w-full">
            In-store and online lay-by payments
          </h1>

          <p class="text-xl font-normal text-grey-dark-300 w-full">
            Offer your customers a credit-free payment option, enabling them to pay online and in-store without interest
            charges.
          </p>
          <div class="flex flex-col-reverse sm:flex-row items-center justify-center lg:justify-start gap-4 mt-6">
            <router-link :to="{ name: 'business-enquiry' }">
              <Button variant="outline-light" size="2xl" class="w-full sm:w-auto py-6">Talk to sales</Button>
            </router-link>
          </div>
        </div>
        <AnimationComponent>
          <img :src="laybyProductImage" alt="LayBy Product Image" class="w-full h-full object-cover" />
        </AnimationComponent>
      </div>
    </section>
    <Separator />
    <section class="w-full py-24 overflow-clip">
      <p class="text-center mb-12 text-grey-dark-300">Join 2,000+ companies already growing with LayUp</p>
      <MerchantLogoCarousel variant="light" />
    </section>
    <Separator />
    <section class="w-full py-24 px-6 lg:px-24">
      <AnimationComponent>
        <div class="flex flex-col items-center justify-center text-center gap-10">
          <HeadingWithIcon
            :icon="getIconComponent('BanknoteArrowUp')"
            title="Streamline payments, boost incremental revenue"
            description="Open new markets and drive a direct lift in sales."
          />
          <div class="w-full max-w-3xl">
            <NumberCounter class="text-boost-500" :counters="revenueCounters" />
          </div>
        </div>
      </AnimationComponent>
    </section>
    <FeaturesSection
      :features="features"
      tag="Features"
      title="Why market leaders are choosing LayUp"
      description="Powering eCommerce and in-store lay-by payments."
    />
    <TitleFeaturesSection
      :features="smartInstallmentFeatures"
      title="Simple, smart instalments"
      description="Transition your business into a new era of digital payments with a digitised recurring payments system."
    />
    <section class="w-full pt-24 px-6 lg:px-24">
      <AnimationComponent>
        <div class="flex flex-col items-center justify-center text-center gap-10 mb-12">
          <h2 class="max-w-3xl text-4xl md:text-5xl font-semibold text-white">
            Automate tracking, reconciliation and reporting
          </h2>
          <p class="max-w-3xl text-lg md:text-xl font-light text-grey-dark-300">
            Collect and manage recurring payments using the merchant dashboard. Get full visibility of each payment plan
            in a centralised cloud-based system.
          </p>
        </div>
      </AnimationComponent>
      <AnimationComponent>
        <div class="flex flex-col items-center justify-center text-center gap-10">
          <img
            :src="automateTrackingReportingImage"
            alt="Automate tracking, reconciliation and reporting"
            class="w-full h-full rounded-t-2xl border-6 border-grey-dark-700 border-b-0 object-cover"
          />
        </div>
      </AnimationComponent>
    </section>
    <PaymentMethodsSection
      title="Payment methods"
      description="Offer a seamless experience where customers can manage their subscriptions directly from their dashboard."
      link-to-page="business-pricing-layby"
    />
    <TitleFeaturesSection
      :features="cloudPaymentFeatures"
      title="Cloud-based payment processing for merchants"
      description="Streamline operations, minimise manual data entry, and improve overall efficiency."
    />
    <ApiFeatures />
    <section class="w-full py-24 px-6 lg:px-24">
      <AnimationComponent>
        <div class="flex flex-col items-center justify-center text-center gap-10">
          <HeadingWithIcon
            :icon="getIconComponent('Zap')"
            title="Unleash lay-by efficiency"
            description="Everything you need to convert, engage, and retain more users."
          />
          <div class="w-full max-w-3xl">
            <NumberCounter class="text-blue-200" :counters="efficiencyCounters" />
          </div>
        </div>
      </AnimationComponent>
    </section>
    <CtaSection
      title="Get started with LayUp"
      description="Low transaction fees and flexible settlements."
      color="grey-dark-900"
    />
  </main>
</template>

<script setup lang="ts">
import automateTrackingReportingImage from '@/assets/images/business/automate-tracking-reporting.webp';
import laybyProductImage from '@/assets/images/business/layby-product-image.webp';
import AnimationComponent from '@/components/AnimationComponent.vue';
import CtaSection from '@/components/business/CtaSection.vue';
import FeaturesSection from '@/components/business/FeaturesSection.vue';
import HeadingWithIcon from '@/components/business/HeadingWithIcon.vue';
import PaymentMethodsSection from '@/components/business/PaymentMethodsSection.vue';
import TitleFeaturesSection from '@/components/business/TitleFeaturesSection.vue';
import MerchantLogoCarousel from '@/components/MerchantLogoCarousel.vue';
import NumberCounter from '@/components/NumberCounter.vue';
import { Button } from '@/components/ui/button';
import Separator from '@/components/ui/separator/SeparatorComponent.vue';
import { getIconComponent } from '@/utils/icons';
import ApiFeatures from '../../components/business/FeatureApi.vue';

const revenueCounters = [
  {
    number: 20,
    unit: '%',
    description: 'Total revenue increase',
  },
  {
    number: 3,
    unit: 'x',
    description: 'Checkout order value',
  },
  {
    number: 40,
    unit: '%',
    description: 'Increased returning customers',
  },
];

const efficiencyCounters = [
  {
    number: 90,
    unit: '%',
    description: 'Lay-by completion rate that surpasses industry standards.',
  },
  {
    number: 45,
    unit: 'hrs',
    description: 'Saved monthly on administrative lay-by tasks.',
  },
  {
    number: 10,
    unit: 'x',
    description: 'More efficient digitised and automated system.',
  },
];

const features = [
  {
    icon: 'Cloud',
    title: 'Reduce admin costs',
    description:
      'LayUp’s cloud-based system automatically tracks, reconciles and collects all payments, handling the hassle for you.',
  },
  {
    icon: 'TrendingUp',
    title: 'Increase completion rates',
    description:
      'We provide an automated recurring payments system that increases the completion rates by up to 60%, immediately affecting revenue.',
  },
  {
    icon: 'ChartLine',
    title: 'Increase purchase value',
    description:
      'Average order values can increase by up to 25% as payments are broken down into more affordable weekly or monthly instalments.',
  },
  {
    icon: 'Handbag',
    title: 'Works across platforms',
    description:
      'Our technology offers easy-to-install plugins for various platforms, or you can utilise our open APIs for custom integrations.',
  },
  {
    icon: 'LockKeyhole',
    title: 'Remove fraud',
    description:
      'Reduce the risk of fraudulent activity with an entirely secure and encrypted system. Know your data and information are safe and protected.',
  },
  {
    icon: 'CreditCard',
    title: 'Accept multiple payment options',
    description:
      'We provide multiple payment options that reduce friction, give customers choices at checkout, and effectively minimise drop-off rates.',
  },
];

const smartInstallmentFeatures = [
  {
    icon: 'MessagesSquare',
    title: 'Centralise',
    description: 'Connect multiple stores with payment infrastructure that provides real time data for business.',
  },
  {
    icon: 'FileDigit',
    title: 'Digitise',
    description: 'Consolidate and transition fragmented financial transactions into a unified, digital platform.',
  },
  {
    icon: 'CalendarSync',
    title: 'Automate',
    description: 'Track and reconcile all online and in-store recurring payments through cloud-based systems.',
  },
];

const cloudPaymentFeatures = [
  {
    icon: 'ClipboardCheck',
    title: 'Track',
    description: 'Online and in-store recurring payments.',
  },
  {
    icon: 'Landmark',
    title: 'Reconcile',
    description: 'Using a treasury reconciliation and settlement system.',
  },
  {
    icon: 'HandCoins',
    title: 'Collect',
    description: 'Automated payment collection and refund capabilities.',
  },
];
</script>
